import React, { useEffect, useContext } from 'react';
import { EditableFieldsContext } from '../../../context';
import { PaymentOptionsContext } from '../context';
import { AddressList } from '../AddressList';

/* Purpose: Allows the user to link an ACH account through PLAID
 *
 * Assumptions:  user does not have a linked ACH account
 *
 * Users with no ACH account will go through 2 flows depending on their addresses:
 *
 * Flow 1: User _has_ addresses
 * 1. Allow the user to select a billing address
 *  1.a. If they wish to add a new one they can press the `add address` button
 *  1.a.1 They will be shown an address form which will return them back to the
   billing address selection once form is _completed_ or _canceled_
 */
interface BaseAddBankAccountFlowProps {
    id: string;
    children: React.ReactNode;
}
export const BaseAddBankAccountFlow = ({ id, children }: BaseAddBankAccountFlowProps) => {
    const { fieldToEdit } = useContext(EditableFieldsContext);
    const contextProps = useContext(PaymentOptionsContext);
    const fieldBeingEdited = id === fieldToEdit;
    const { setCreateMode, addresses } = contextProps;

    useEffect(() => {
        if (addresses.length === 0) {
            setCreateMode(true);
        } else {
            setCreateMode(false);
        }
    }, [setCreateMode, addresses]);

    const addNewAddress = (e: React.MouseEvent<HTMLParagraphElement>) => {
        e.preventDefault();
        setCreateMode(true);
    };

    if (!fieldBeingEdited) {
        return <>{children}</>;
    } else if (contextProps.addresses.length > 0) {
        // CASE: prompt user to select an address to begin plaid flow
        return (
            <div>
                <p className="text-grey-1 text-sm mb-2">Select the address tied to your account</p>
                <AddressList {...contextProps} />
                {!contextProps.createMode && (
                    <p
                        onClick={addNewAddress}
                        className="underline text-brand text-xs cursor-pointer"
                    >
                        Add New Address
                    </p>
                )}
            </div>
        );
    } else {
        // CASE: user does not have any address in their account
        // prompt to add a new one.
        return (
            <p className="text-grey-1 text-sm mb-2">Add the address tied to your bank account</p>
        );
    }
};
